// Top-level Access Points list. Mirrors RTLSMobile's AP CRUD: every field
// (name, SSID, MAC, IP, x/y/z, level, radius, status) is editable. APs are
// linked to a parent Location via location_id; the table column shows the
// parent location's name when known.

import React, {useMemo, useState} from 'react'
import clsx from 'clsx'
import {useNavigate} from 'react-router-dom'
import {Pencil, Plus} from 'lucide-react'
import {SearchBar} from '../components/common/SearchBar'
import {SummaryCard} from '../components/common/SummaryCard'
import {useSupabaseSubscription} from '../app/useSupabaseSubscription'
import type {APDetail} from './location/types'

interface APRow extends APDetail {}

interface LocationMini {
  id: string
  name: string
  x: number | null
  y: number | null
  z: number | null
}

const STATUS_DOT: Record<APDetail['status'], string> = {
  active:  'bg-statusGreen',
  offline: 'bg-textMuted',
  fault:   'bg-statusRed',
}

const STATUS_LABEL: Record<APDetail['status'], string> = {
  active:  'Active',
  offline: 'Offline',
  fault:   'Fault',
}

export const AccessPointsScreen: React.FC = () => {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')

  const {rows: aps, loading: aLoading, error: aError} =
    useSupabaseSubscription<APRow>({
      table: 'access_points',
      columns:
        'id, location_id, name, mac_address, ssid, ip_address, is_active, status, ' +
        'x, y, z, level, radius_m',
      orderBy: ['name', true],
    })

  const {rows: locations, loading: lLoading, error: lError} =
    useSupabaseSubscription<LocationMini>({
      table: 'locations',
      columns: 'id, name, x, y, z',
    })

  const locById = useMemo(() => {
    const m = new Map<string, LocationMini>()
    for (const l of locations) m.set(l.id, l)
    return m
  }, [locations])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return aps
    return aps.filter(a => {
      const parentName = a.location_id ? locById.get(a.location_id)?.name ?? '' : ''
      return (
        a.name.toLowerCase().includes(q) ||
        (a.mac_address ?? '').toLowerCase().includes(q) ||
        (a.ssid ?? '').toLowerCase().includes(q) ||
        parentName.toLowerCase().includes(q)
      )
    })
  }, [aps, locById, search])

  const summary = useMemo(() => {
    let active = 0
    let offline = 0
    let fault = 0
    for (const a of aps) {
      if (a.status === 'active') active++
      else if (a.status === 'offline') offline++
      else if (a.status === 'fault') fault++
    }
    return {total: aps.length, active, offline, fault}
  }, [aps])

  if (aLoading || lLoading) {
    return <div className="flex h-full items-center justify-center text-textMuted">Loading access points…</div>
  }
  if (aError || lError) {
    return (
      <div className="flex h-full items-center justify-center p-xxxl">
        <div className="rounded-lg border border-statusRed bg-surfaceCard p-xxl">
          <h3 className="mb-md text-lg font-semibold text-statusRed">Fetch failed</h3>
          <pre className="whitespace-pre-wrap font-mono text-sm text-textPrimary">{aError || lError}</pre>
        </div>
      </div>
    )
  }

  const openAdd = () => {
    if (locations.length === 0) {
      window.alert('No locations exist yet. Add a location before creating an AP.')
      return
    }
    navigate('/access-points/add')
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-background">
      <div className="px-xl py-md">
        <div className="flex items-center gap-lg">
          <div className="flex flex-1 items-center gap-lg rounded-lg bg-surface p-sm">
            <div className="flex-1">
              <SearchBar value={search} onChange={setSearch} placeholder="Search.." />
            </div>
          </div>
          <button
            type="button"
            onClick={openAdd}
            className="flex items-center gap-sm rounded-md bg-primary px-xl py-md text-md font-bold text-black hover:bg-primaryLight"
          >
            <Plus size={16} color="black" strokeWidth={3} />
            Add Access Point
          </button>
        </div>
      </div>

      <div className="mb-lg grid grid-cols-4 gap-md px-xl">
        <SummaryCard
          label="Total"
          value={summary.total}
          accentColor="var(--color-primary)"
        />
        <SummaryCard
          label="Active"
          value={summary.active}
          valueClass="text-statusGreen"
          accentColor="var(--color-statusGreen)"
        />
        <SummaryCard
          label="Offline"
          value={summary.offline}
          valueClass="text-textMuted"
          accentColor="var(--color-textMuted)"
        />
        <SummaryCard
          label="Fault"
          value={summary.fault}
          valueClass="text-statusRed"
          accentColor="var(--color-statusRed)"
        />
      </div>

      <div className="mx-xl mb-xl flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-surfaceBorder bg-surfaceCard">
        <div className="grid shrink-0 grid-cols-[2fr_1.5fr_1.2fr_1fr_1fr_60px] items-center border-b border-divider bg-surface px-lg py-md text-xs font-semibold uppercase text-textMuted">
          <div>Name</div>
          <div>Parent Location</div>
          <div>MAC Address</div>
          <div>SSID</div>
          <div>Status</div>
          <div className="text-center">Actions</div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {filtered.map(ap => {
            const parent = ap.location_id ? locById.get(ap.location_id) : null
            return (
              <button
                key={ap.id}
                type="button"
                onClick={() => navigate(`/access-points/${ap.id}`)}
                className={clsx(
                  'grid w-full grid-cols-[2fr_1.5fr_1.2fr_1fr_1fr_60px] items-center px-lg py-md text-left',
                  'border-b border-divider hover:bg-surfaceLight/40 focus:bg-surfaceLight/60 focus:outline-none',
                )}
              >
                <div className="truncate text-md font-semibold text-textPrimary">{ap.name}</div>
                <div className="truncate text-md text-textPrimary">{parent?.name ?? '—'}</div>
                <div className="truncate font-mono text-sm text-textSecondary">{ap.mac_address ?? '—'}</div>
                <div className="truncate text-md text-textSecondary">{ap.ssid ?? '—'}</div>
                <div className="flex items-center gap-sm">
                  <span className={clsx('h-2 w-2 rounded-full', STATUS_DOT[ap.status])} />
                  <span className="text-md font-medium text-textPrimary">{STATUS_LABEL[ap.status]}</span>
                </div>
                <div className="flex justify-center">
                  <span
                    role="button"
                    tabIndex={-1}
                    onClick={ev => {
                      ev.stopPropagation()
                      navigate(`/access-points/${ap.id}/edit`)
                    }}
                    className="flex h-9 w-9 items-center justify-center rounded-md border border-surfaceBorder text-textPrimary hover:border-primary hover:text-primary"
                    aria-label={`Edit ${ap.name}`}
                  >
                    <Pencil size={14} />
                  </span>
                </div>
              </button>
            )
          })}
          {filtered.length === 0 && (
            <div className="px-lg py-xxxl text-center text-textMuted">
              No access points match "{search}".
            </div>
          )}
        </div>
      </div>

    </div>
  )
}
